import {
  LoadingAlert,
  Table,
  WarningAlert,
  YieldBearingAssetListItem,
} from '@/components';
import {
  TableCellProps,
  TableRowProps,
} from '@/components/molecules/Table/interfaces/Table';
import RatingBarDial from '@/components/organisms/RatingBarDial/RatingBarDial';
import { useYieldBearingAssets } from '@/components/providers/postauth/YieldBearingAssetsProvider';
import { YieldBearingAssetInterface } from '@/interfaces/YieldBearingAssetInterface';
import { URLS } from '@/utils/constants';
import {
  formatNumberToAmountInUsdString,
  formatNumberToAPYString,
} from '@/utils/formatUtils';
import { logError, logInfo, logWarning } from '@/utils/logger';
import { LABELS, MESSAGES } from '@/utils/strings';
import { FC, useMemo, useState } from 'react';
import ReturnBarDial from '../ReturnBarDial/ReturnBarDial';
import RiskBarDial from '../RiskBarDial/RiskBarDial';
export enum InformationAmount {
  LEAST_INFORMATION = 'least_information',
  FULL_INFORMATION = 'full_information',
}
interface YieldBearingAssetsListProps {
  filters?: {
    limit?: number;
    riskType?: string;
    returnType?: string;
    minApy?: number;
    maxApy?: number;
    minMcap?: number;
    maxMcap?: number;
    tickers?: string[];
  };
  amountOfInformationToShow?: InformationAmount;
  onRowClick?: (asset: YieldBearingAssetInterface) => void;
}
const YieldBearingAssetsList: FC<YieldBearingAssetsListProps> = ({
  filters = {},
  amountOfInformationToShow = InformationAmount.FULL_INFORMATION,
  onRowClick,
}) => {
  const { yieldBearingAssets, error, loading } = useYieldBearingAssets();
  const [sortedColumn, setSortedColumn] = useState<number>(-1);
  const [isAscending, setIsAscending] = useState<boolean>(true);
  const filteredAssets = useMemo(() => {
    return yieldBearingAssets.filter((asset) => {
      const matchesRiskType =
        !filters.riskType || asset.riskType === filters.riskType;
      const matchesReturnType =
        !filters.returnType || asset.returnType === filters.returnType;
      const matchesMinApy = !filters.minApy || asset.apy >= filters.minApy;
      const matchesMaxApy = !filters.maxApy || asset.apy <= filters.maxApy;
      const matchesMinMcap = !filters.minMcap || asset.mcap >= filters.minMcap;
      const matchesMaxMcap = !filters.maxMcap || asset.mcap <= filters.maxMcap;
      return (
        matchesRiskType &&
        matchesReturnType &&
        matchesMinApy &&
        matchesMaxApy &&
        matchesMinMcap &&
        matchesMaxMcap
      );
    });
  }, [
    yieldBearingAssets,
    filters.riskType,
    filters.returnType,
    filters.minApy,
    filters.maxApy,
    filters.minMcap,
    filters.maxMcap,
    filters.tickers,
  ]);
  const limitedAssets = useMemo(() => {
    let assets = filteredAssets;
    if (filters.tickers) {
      assets = assets.filter((asset) =>
        filters.tickers?.includes(asset.ticker),
      );
    }
    if (filters.limit) {
      if (filters.tickers && assets.length < filters.limit) {
        const remaining = filters.limit - assets.length;
        const additionalAssets = filteredAssets
          .filter((asset) => !filters.tickers?.includes(asset.ticker))
          .sort((a, b) => b.returnScore - a.returnScore)
          .slice(0, remaining);
        assets = [...assets, ...additionalAssets].slice(0, filters.limit);
      } else {
        assets = assets.slice(0, filters.limit);
      }
    }
    return assets;
  }, [filteredAssets, filters.limit, filters.tickers]);
  if (loading && yieldBearingAssets.length === 0) {
    logInfo(
      'Loading yield-bearing assets, but none found yet',
      'YieldBearingAssetsList',
    );
    return (
      <LoadingAlert
        title={MESSAGES.YIELD_BEARING_ASSETS.LOADING_TITLE}
        description={MESSAGES.YIELD_BEARING_ASSETS.LOADING_DESCRIPTION}
      />
    );
  }
  if (!loading && yieldBearingAssets.length === 0) {
    logWarning('No yield-bearing assets found', 'YieldBearingAssetsList');
    return (
      <WarningAlert
        title={
          MESSAGES.YIELD_BEARING_ASSETS.NO_YIELD_BEARING_ASSETS_FOUND_TITLE
        }
        description={
          MESSAGES.YIELD_BEARING_ASSETS
            .NO_YIELD_BEARING_ASSETS_FOUND_DESCRIPTION
        }
      />
    );
  }
  if (!loading && filteredAssets.length === 0) {
    logWarning(
      'No filtered yield-bearing assets found',
      'YieldBearingAssetsList',
    );
    return (
      <WarningAlert
        title={
          MESSAGES.YIELD_BEARING_ASSETS
            .NO_FILTERED_YIELD_BEARING_ASSETS_FOUND_TITLE
        }
        description={
          MESSAGES.YIELD_BEARING_ASSETS
            .NO_FILTERED_YIELD_BEARING_ASSETS_FOUND_DESCRIPTION
        }
      />
    );
  }
  if (error) {
    logError('Error loading yield-bearing assets', 'YieldBearingAssetsList', {
      error,
    });
  }
  const handleSort = (columnIndex: number) => {
    if (sortedColumn === columnIndex) {
      setIsAscending(!isAscending);
    } else {
      setSortedColumn(columnIndex);
      setIsAscending(true);
    }
  };
  const getComparableValue = (
    asset: YieldBearingAssetInterface,
    columnIndex: number,
  ): string | number => {
    switch (columnIndex) {
      case 0:
        return asset.name;
      case 1:
        return asset.apy;
      case 2:
        return asset.mcap;
      case 3:
        return asset.returnScore;
      case 4:
        return asset.riskScore;
      case 5:
        return asset.ratings?.overallMetrics.averageRating || 0;
      default:
        return '';
    }
  };
  const sortedYieldBearingAssets =
    sortedColumn !== -1
      ? [...limitedAssets].sort((a, b) => {
          const aValue = getComparableValue(a, sortedColumn);
          const bValue = getComparableValue(b, sortedColumn);
          if (aValue < bValue) return isAscending ? -1 : 1;
          if (aValue > bValue) return isAscending ? 1 : -1;
          return 0;
        })
      : limitedAssets;
  const rows: TableRowProps[] = sortedYieldBearingAssets.map(
    (asset: YieldBearingAssetInterface) => {
      const cells: TableCellProps[] = [
        {
          mainText: <YieldBearingAssetListItem yieldBearingAsset={asset} />,
        },
      ];
      if (amountOfInformationToShow === InformationAmount.LEAST_INFORMATION) {
        cells.push({
          mainText: formatNumberToAPYString(asset.apy),
          subText: formatNumberToAmountInUsdString(asset.mcap),
          align: 'right',
        });
      } else {
        cells.push(
          {
            mainText: formatNumberToAPYString(asset.apy),
          },
          {
            mainText: formatNumberToAmountInUsdString(asset.mcap),
          },
          {
            mainText: <ReturnBarDial asset={asset} />,
            className: 'mt-1',
          },
          {
            mainText: <RiskBarDial asset={asset} />,
            className: 'mt-1',
          },
          {
            mainText: <RatingBarDial asset={asset} />,
            className: 'mt-1',
          },
        );
      }
      return onRowClick
        ? { cells }
        : {
            navigateToUrl: `${URLS.allYieldBearingAssets}/${asset.ticker}`,
            cells,
          };
    },
  );
  const columnHeaders =
    amountOfInformationToShow === InformationAmount.LEAST_INFORMATION
      ? [
          LABELS.TRENDING_YIELD_BEARING_ASSETS_CARD.CARD_BODY
            .TABLE_COLUMN_HEADERS.ASSET_NAME,
          {
            value:
              LABELS.TRENDING_YIELD_BEARING_ASSETS_CARD.CARD_BODY
                .TABLE_COLUMN_HEADERS.PERFORMANCE,
            align: 'right' as 'right',
          },
        ]
      : [
          LABELS.ALL_YIELD_BEARING_ASSETS_CARD.CARD_BODY.TABLE_COLUMN_HEADERS
            .ASSET_NAME,
          LABELS.ALL_YIELD_BEARING_ASSETS_CARD.CARD_BODY.TABLE_COLUMN_HEADERS
            .PERFORMANCE,
          LABELS.ALL_YIELD_BEARING_ASSETS_CARD.CARD_BODY.TABLE_COLUMN_HEADERS
            .MARKET_CAP,
          LABELS.ALL_YIELD_BEARING_ASSETS_CARD.CARD_BODY.TABLE_COLUMN_HEADERS
            .RETURN_PROFILE,
          LABELS.ALL_YIELD_BEARING_ASSETS_CARD.CARD_BODY.TABLE_COLUMN_HEADERS
            .RISK_PROFILE,
          LABELS.ALL_YIELD_BEARING_ASSETS_CARD.CARD_BODY.TABLE_COLUMN_HEADERS
            .USER_SCORE,
        ];
  const columnWidths =
    amountOfInformationToShow === InformationAmount.LEAST_INFORMATION
      ? [65, 35]
      : [25, 15, 15, 15, 15, 15];
  const sortable =
    amountOfInformationToShow === InformationAmount.LEAST_INFORMATION
      ? [true, true]
      : [true, true, true, true, true, true];
  const handleRowClick = (asset: YieldBearingAssetInterface) => {
    if (onRowClick) {
      onRowClick(asset);
    }
  };
  return (
    <Table
      rightAlignLastColumn={
        amountOfInformationToShow === InformationAmount.LEAST_INFORMATION
          ? true
          : false
      }
      columnHeaders={columnHeaders}
      columnWidths={columnWidths}
      rows={rows}
      sortable={sortable}
      onSort={handleSort}
      sortedColumn={sortedColumn}
      isAscending={isAscending}
      onClickRow={(event, rowIndex) =>
        handleRowClick(sortedYieldBearingAssets[rowIndex])
      }
    />
  );
};
export default YieldBearingAssetsList;
